import React, { Component } from "react";
import PropTypes from "prop-types";
import { has } from "lodash";
import { connect } from "react-redux";
import { Route, withRouter } from "react-router-dom";
import { ContentProvider, Types } from "../../../../CommonComponents/ContentMessages";
import RecommendFlow from "../../../../CommonComponents/RecommendFlow/RecommendFlow";
import { formatAmount, initiateTracking, recordError } from "../../../../utils/commonUtil";
import { getPrimaryAccount, isGeminiUser } from '../../../../utils/sessionUtil';
import { newGrant } from "./actions";
import Alert from "./Component/Alert/Alert";
import CharitySelect from "./Component/CharitySelect/CharitySelect";
import GrantScheduleAllocation from "./Component/GrantScheduleAllocation/GrantScheduleAllocation";
import { RecommendGrantStep } from "./Component/InfoRectangle/InfoRectangle";
import PurposeRecognition from "./Component/PurposeRecognition/PurposeRecognition";
import RecurringGrantSchedule from "./Component/RecurringGrantSchedule/RecurringGrantSchedule";
import ReviewGrant from "./Component/ReviewGrant/ReviewGrant";
import { CharityChangeLink, ImpactContainer, TableLabel, UnderlyingContent, GrantSectionHeading } from "./style";
import { mapAllocationTypeLabel, recognitionMap } from "./utils";
import { availableBalance, makeUserSessionSuccess } from "../../../../actions/globalActions";

import { recommendGrantPageDerivedStates } from './Selectors/RecommendGrantPageSelectors';


export class RecommendGrantPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stepConfig: this.stepConfig()
    };
    this.pageTitle = "Recommend a Grant";
  }

  componentDidMount() {
    window.scrollTo(0,0);
    // Clean up any existing data in store for recommend grant flow
    if (this.props.location.pathname === '/grants/recommend') {
      this.props.newGrant();
    }
    // Check user data and primary account
    this.checkSessionData() && this.props.setUserSession()
  }
  componentWillMount() {
    this.props.getAvailableBalance({
      metaData: [
        {
          method: 'GET',
          url: `/donor-portal/api/account/${
            getPrimaryAccount().userId
            }/findBalance`
        }
      ]
    });
  }
  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      window.scrollTo(0, 0)
    }
    // Reset values in store when coming from menu (fixes bug 5409)
    if (prevProps.location.pathname !== '/grants/recommend'
      && this.props.location.state && this.props.location.state.fromMenu) {
      //Once done don't repeat
      const stateCopy = { ...this.props.location.state };
      delete stateCopy.fromMenu;
      this.props.history.replace({ state: stateCopy });
      //Reset values as you came again from menu. Sigh!!
      this.props.newGrant();
    }
  }

  checkSessionData = () => isGeminiUser() && has(sessionStorage, 'userData') && has(sessionStorage, 'primaryAccount');

  onUpdate = () => {
    this.props.history.push('/grants/recommend/review');
  }

  analyticsHandler = (dataLayerName, event, name) => {
    initiateTracking(dataLayerName, this.pageTitle, event, name);
  }

  onUpdateCancel = (step, stepNum, scheduleType) => {
    try {
      (step && stepNum) && this.analyticsHandler('cancel', `Step ${stepNum} ${step}`, `Cancel Edit${scheduleType ? scheduleType === 'recurring' ? ` - Recurring Grant` : ` - One Time Grant` : ''}`);
      this.props.history.push('/grants/recommend/review');
    } catch (error) {
      recordError(error);
    }
  }

  chooseDiffCharity = () => {
    try {
      const { charityBackPath, history } = this.props;
      initiateTracking('chooseCharity', 'Recommend A Grant', 'Step 1 Select charity and purpose', 'Choose a different charity');
      if (charityBackPath) {
        history.push(charityBackPath);
      } else {
        history.push("/grants/recommend");
      }
    } catch (error) {
      recordError(error);
    }
  }
  // Checks for current page through location and returns the stepper configuration for analytics
  stepSelector = (location, step) => {
    if (location.pathname === '/grants/recommend/allocation-schedule') return `Step 2 ${step.stepTwo}`
    return `Step 3 ${step.stepThree}`
  }

  stepConfig = () => {
    const stepDesc = {
      stepOne: "Select charity and purpose",
      stepTwo: "Select allocation and schedule",
      stepThree: "Review and submit"
    };
    const selectedAccountId = this.props.selectedAccountId || getPrimaryAccount().userId;
    return [{
      key: 1,
      route: '/grants/recommend',
      description: stepDesc.stepOne,
      stepContent: () => {
        const {
          selectedCharityName,
          grantPurposeName,
          grantRecognitionId,
          scheduleType,
          location
        } = this.props;
        const grantRecognitionName = recognitionMap[grantRecognitionId];
        return (
          <RecommendGrantStep prefixId="rag-charity-purpose" link="/grants/recommend/charity-purpose" analyticsTracking={ () => {
            this.analyticsHandler('edit', this.stepSelector(location, stepDesc), `Edit - ${scheduleType === 'oneTime' ? `One Time Grant` : `Recurring Grant`} - Step 1 ${stepDesc.stepOne} - Stepper`)}}>
            <div>
              <div>{selectedCharityName}</div>
              <div>{grantPurposeName && "Purpose: " + this.props.grantPurposeName}</div>
              <div>{grantRecognitionName && "Recognition: " + grantRecognitionName}</div>
            </div>
          </RecommendGrantStep>
        );
      },
      subRoutes: [{
        path: '/grants/recommend/charity-purpose',
        component: () => (
          <PurposeRecognition
            {...this.props}
            stepDesc={stepDesc.stepOne}
            accountId={selectedAccountId}
            onUpdate={this.onUpdate}
            onUpdateCancel={this.onUpdateCancel}
            analyticsHandler={this.analyticsHandler}
            page={'Recommend a Grant'}
            keyPropertyUpdate={"Cancel Edit Recommend a Grant"}
            enableCancelPopUp
          />
        )
      }],
      mainContent: () => (
        <>
          <CharitySelect
            {...this.props}
            stepDesc={stepDesc.stepOne}
            accountId={selectedAccountId}
            onUpdate={this.onUpdate}
            onUpdateCancel={this.onUpdateCancel}
            analyticsHandler={this.analyticsHandler}
          />
        </>
      )
    }, {
      key: 2,
      route: '/grants/recommend/allocation-schedule',
      description: stepDesc.stepTwo,
      stepContent: () => {
        const {
          scheduleType,
          grantAmount,
          startDate,
          userSelectedDateVal,
          allocationType,
          noOfPayments,
          selectedAllSharesAllPools,
          selectedAllShares
        } = this.props;
        const oneTimeStepPreview = (
          <div >
            <div>One-time Grant</div>
            <div>{(userSelectedDateVal === '' || userSelectedDateVal === 'ASAP') ? 'ASAP' : startDate}</div>
            {grantAmount && <div> {formatAmount(grantAmount)}</div>}
            <div>{mapAllocationTypeLabel(allocationType, selectedAllSharesAllPools, selectedAllShares)}</div>
          </div >
        );
        const recurringPreview = (
          <div >
            <div>Recurring Grant</div>
            <div>{`${noOfPayments} payments for a total of ${formatAmount(noOfPayments * grantAmount)}`}</div>
            <div>Starting {startDate}</div>
            <div>{mapAllocationTypeLabel(allocationType, selectedAllSharesAllPools, selectedAllShares)}</div>
          </div >
        );
        return (<RecommendGrantStep prefixId="rag-allocation-schedule" link="/grants/recommend/allocation-schedule" analyticsTracking={ () => this.analyticsHandler('edit', `Step 3 ${stepDesc.stepThree}`, `Edit - ${scheduleType === 'oneTime' ? `One Time Grant` : `Recurring Grant`} - Step 2 ${stepDesc.stepTwo} - Stepper`)}>
          {scheduleType === 'oneTime' ? oneTimeStepPreview : recurringPreview}
        </RecommendGrantStep>)
      },
      subRoutes: [{
        path: '/grants/recommend/step2A',
        component: () => <RecurringGrantSchedule stepDesc={stepDesc.stepTwo} onUpdate={this.onUpdate} onUpdateCancel={this.onUpdateCancel} analyticsHandler={this.analyticsHandler} enableCancelPopUp />
      }],
      mainContent: () => (
        <GrantScheduleAllocation
          {...this.props}
          stepDesc={stepDesc.stepTwo}
          accountId={selectedAccountId}
          onUpdate={this.onUpdate}
          analyticsHandler={this.analyticsHandler}
          onUpdateCancel={this.onUpdateCancel}
          page={'Recommend a Grant'}
          keyPropertyUpdate={"Cancel Edit Recommend a Grant"}
          enableCancelPopUp
        />
      )
    },
    {
      key: 3,
      route: '/grants/recommend/review',
      description: stepDesc.stepThree,
      mainContent: () =>
        <ReviewGrant
          {...this.props}
          stepNum={3}
          stepDesc={stepDesc.stepThree}
          analyticsHandler={this.analyticsHandler}
          accountId={selectedAccountId}
        />
    }];
  }

  renderSubHeader = () => {
    const { history } = this.props;
    return (
      <>
        <Route
          key="/grants/recommend"
          path="/grants/recommend"
          render={() => (
            <>
              <Alert
                type="Notice: "
                heading="field_notice_content"
                body="field_show_more_content"
                page={this.pageTitle}
                contentType={Types.PAGE_NOTICES}
                history={history}               
              />
              {
                this.props.editMode &&
                <CharityChangeLink>
                  <TableLabel as="span">Don’t need to change the charity?</TableLabel>&nbsp;
                  <UnderlyingContent id="anrUnderlyingContent" onClick={this.onUpdateCancel}>
                    Go back to review and submit.
                  </UnderlyingContent>
                </CharityChangeLink>
              }
            </>
          )}
          exact
        />

        <Route
          key="/grants/recommend/charity-purpose"
          path="/grants/recommend/charity-purpose"
          render={() => (
            this.props.selectedCharityName ? (
            <CharityChangeLink>
              <TableLabel as="span">{`You have selected ${this.props.selectedCharityName}. Not the charity you wanted? `}</TableLabel>
              <UnderlyingContent id="anrDiffCharity"onClick={this.chooseDiffCharity}>
                Choose a different charity.
                </UnderlyingContent>
            </CharityChangeLink>
            ): ""
          )}
          exact
        />
      </>
    );
  }

  render() {
    const {
      availableBalance,
      currentBalance,
      selectedAccountId,
      history
    } = this.props;
    const breadCrumbConfig = [
      {
        routeName: 'Home',
        path: '/',
      },
      {
        routeName: 'Grants',
        path: '/grants',
      },
      {
        routeName: 'Recommend a Grant',
        path: '/grants/recommend',
      },
    ];
    return (
      <ContentProvider types={[Types.PAGE_NOTICES, Types.PAGE_TITLE_DESCRIPTION, Types.PAGE_ALERTS, Types.PAGE_PLEASE_NOTE, Types.PAGE_PERMISSION_TO_PUBLISH, Types.PAGE_CHECKBOX]} pages={['Recommend a Grant']}>
        <ImpactContainer>
          <RecommendFlow
            userId={selectedAccountId}
            accData={{
              availableBalance,
              currentBalance,
            }}
            breadcrumbRoutes={breadCrumbConfig}
            pageHeader="Recommend a Grant"
            stepConfig={this.state.stepConfig}
            beforeMainContent={this.renderSubHeader}
            history={history}
            featureCode="RAG"
          />
        </ImpactContainer>
      </ContentProvider>
    );
  }
}

const mapStateToProps = state => ({
  ...recommendGrantPageDerivedStates(state.toJS()),
});

export const mapDispatchToProps = dispatch => ({
  newGrant: data => dispatch(newGrant(data)),
  getAvailableBalance: data => dispatch(availableBalance(data)),
  setUserSession: () => dispatch(makeUserSessionSuccess())
});

RecommendGrantPage.propTypes = {
  allocationType: PropTypes.string,
  availableBalance: PropTypes.number,
  charityBackPath: PropTypes.string,
  currentBalance: PropTypes.number,
  editMode: PropTypes.bool,
  getAvailableBalance: PropTypes.func,
  grantAmount: PropTypes.number,
  grantPurposeName: PropTypes.string,
  grantRecognitionId: PropTypes.string,
  history: PropTypes.object,
  location: PropTypes.object,
  newGrant: PropTypes.func,
  noOfPayments: PropTypes.number,
  scheduleType: PropTypes.string,
  selectedAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedAllShares: PropTypes.bool,
  selectedAllSharesAllPools: PropTypes.bool,
  selectedCharityEin: PropTypes.string,
  selectedCharityName: PropTypes.string,
  setUserSession: PropTypes.func,
  startDate: PropTypes.string,
  staticContext: PropTypes.any,
  userSelectedDateVal: PropTypes.any,
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(RecommendGrantPage)
);
